import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { EmotionPopover } from './emotion-popover'
import { AVAILABLE_EMOTIONS } from './constants'

interface RichScriptEditorProps {
    text: string
    onTextChange: (text: string) => void
    onTextBlur: () => void
    onTextFocus: () => void
}

/**
 * Tag tokens we recognize in line text:
 *   1) Legacy SSML-style emotion tag (Cartesia / non-series podcasts):
 *        <emotion value="happy" />
 *   2) New Gemini transcript audio tag (series + future content):
 *        [excited], [laughs], [short pause], ...
 *
 * Both styles render as a chip in the editor. We preserve the original style on
 * serialization so legacy podcasts keep their SSML and new podcasts keep brackets.
 * Newly inserted chips always serialize as brackets (Gemini format).
 *
 * Capture groups:
 *   1: emotion value from <emotion value="..." />
 *   2: contents of [tag]
 */
// Bracket tags accept letters, digits, hyphens, underscores, spaces (e.g. [short pause]).
// Length cap keeps obviously-not-a-tag content (e.g. [1, 2, 3]) out, and avoids matching
// runaway brackets across newlines.
const TOKEN_REGEX = /<emotion\s+value=["']([^"']+)["']\s*\/>|\[([a-zA-Z][a-zA-Z0-9 _-]{0,40})\]/g

type TagStyle = 'ssml' | 'bracket'

interface TagSegment {
    type: 'tag'
    value: string
    style: TagStyle
}

interface TextSegment {
    type: 'text'
    value: string
}

type Segment = TextSegment | TagSegment

/**
 * Splits text into segments being either raw string or a tag chip.
 */
function parseText(text: string): Segment[] {
    const segments: Segment[] = []
    let lastIndex = 0
    let match

    // Reset regex state just in case
    TOKEN_REGEX.lastIndex = 0

    while ((match = TOKEN_REGEX.exec(text)) !== null) {
        if (match.index > lastIndex) {
            segments.push({ type: 'text', value: text.slice(lastIndex, match.index) })
        }
        if (match[1] !== undefined) {
            // <emotion value="..." />
            segments.push({ type: 'tag', value: match[1], style: 'ssml' })
        } else if (match[2] !== undefined) {
            // [tag]
            segments.push({ type: 'tag', value: match[2], style: 'bracket' })
        }
        lastIndex = match.index + match[0].length
    }
    if (lastIndex < text.length) {
        segments.push({ type: 'text', value: text.slice(lastIndex) })
    }
    return segments
}

export const RichScriptEditor: React.FC<RichScriptEditorProps> = ({
    text,
    onTextChange,
    onTextBlur,
    onTextFocus
}) => {
    const containerRef = useRef<HTMLDivElement>(null)

    // Internal state to track if we are processing an input event vs external prop update
    const isInternalUpdate = useRef(false)

    // State for the popover (edit mode)
    const [editingAnchor, setEditingAnchor] = useState<HTMLElement | null>(null)

    // State for autocomplete
    const [autocompleteOpen, setAutocompleteOpen] = useState(false)
    const [autocompleteCoords, setAutocompleteCoords] = useState<{ top: number; left: number } | null>(null)
    const [autocompleteSearch, setAutocompleteSearch] = useState('')

    /**
     * Reconstruct the full text string from the DOM nodes.
     * Walks through child nodes:
     *  - Text nodes: append text content
     *  - Elements with data-emotion: append the original tag style preserved on the chip
     *    (`ssml` → <emotion value="..." />, `bracket` → [tag]).
     */
    const getHtmlContentAsText = useCallback(() => {
        if (!containerRef.current) return ''
        let result = ''

        // Recursive walker
        const walk = (node: Node) => {
            if (node.nodeType === Node.TEXT_NODE) {
                result += node.textContent
            } else if (node.nodeType === Node.ELEMENT_NODE) {
                const el = node as HTMLElement
                if (el.dataset.emotion) {
                    const style = (el.dataset.tagStyle as TagStyle | undefined) ?? 'bracket'
                    if (style === 'ssml') {
                        result += `<emotion value="${el.dataset.emotion}" />`
                    } else {
                        result += `[${el.dataset.emotion}]`
                    }
                } else if (el.tagName === 'BR') {
                    // Browsers add <br> mostly for visual line breaks or empty blocks. 
                } else {
                    node.childNodes.forEach(walk)
                }
            }
        }
        containerRef.current.childNodes.forEach(walk)
        return result
    }, [])

    const triggerChange = useCallback(() => {
        const newText = getHtmlContentAsText()
        isInternalUpdate.current = true
        onTextChange(newText)
    }, [getHtmlContentAsText, onTextChange])

    /**
     * Visual style for a chip. We treat a handful of "non-verbal sound" tags (laughs,
     * sighs, etc.) differently so they stand out from emotion/pacing tags.
     */
    const isSoundTag = (tag: string) => {
        const t = tag.toLowerCase()
        return t === 'laughter' || t === 'laughs' || t === 'giggles' || t === 'sighs' || t === 'gasp' || t === 'cough' || t === 'breath'
    }

    const createChip = useCallback((emotion: string, style: TagStyle = 'bracket') => {
        const span = document.createElement('span')
        span.contentEditable = "false" // Make the chip itself atomic
        const sound = isSoundTag(emotion)
        span.className = sound
            ? "inline-flex items-center px-1.5 py-0.5 mx-1 rounded text-xs font-medium bg-green-100 text-green-800 select-none cursor-pointer align-middle"
            : "inline-flex items-center px-1.5 py-0.5 mx-1 rounded text-xs font-medium bg-blue-100 text-blue-800 select-none cursor-pointer align-middle"

        span.dataset.emotion = emotion
        span.dataset.tagStyle = style

        const textSpan = document.createElement('span')
        textSpan.innerText = emotion
        span.appendChild(textSpan)

        // Delete button
        const xBtn = document.createElement('span')
        xBtn.className = sound
            ? "ml-1 hover:text-green-950 cursor-pointer flex items-center"
            : "ml-1 hover:text-blue-950 cursor-pointer flex items-center"

        xBtn.innerHTML = `<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>`

        // Prevent editor focus loss when clicking X
        xBtn.addEventListener('mousedown', (e) => {
            e.preventDefault()
            e.stopPropagation()
            span.remove()
            triggerChange()
        })

        span.appendChild(xBtn)

        // Add click listener for editing
        span.addEventListener('click', () => {
            setEditingAnchor(span)
        })

        return span
    }, [triggerChange])

    // --- Rendering to DOM ---

    // We use a simplified approach:
    // On init or external text change (if not internal), wipe content and rebuild DOM.
    const renderContentToDom = useCallback((rawText: string) => {
        if (!containerRef.current) return
        containerRef.current.innerHTML = ''
        const parts = parseText(rawText)

        parts.forEach(part => {
            if (part.type === 'text') {
                const span = document.createElement('span')
                span.textContent = part.value
                containerRef.current?.appendChild(span)
            } else {
                const chip = createChip(part.value, part.style)
                containerRef.current?.appendChild(chip)
            }
        })
    }, [createChip])

    useEffect(() => {
        if (isInternalUpdate.current) {
            isInternalUpdate.current = false
            return
        }
        if (!containerRef.current) return

        // Don't rebuild if text matched what we think it is (avoid cursor jump on identical re-renders)
        const currentContent = getHtmlContentAsText()
        if (currentContent === text) return

        renderContentToDom(text)
    }, [text, getHtmlContentAsText, renderContentToDom])

    const handleInput = useCallback(() => {
        // Check for '@' trigger
        // We need to find the cursor position and see if the char before is '@'
        const sel = window.getSelection()
        if (sel && sel.rangeCount > 0) {
            const range = sel.getRangeAt(0)
            // Check text node content before cursor
            if (range.startContainer.nodeType === Node.TEXT_NODE) {
                const textNode = range.startContainer
                const textContent = textNode.textContent || ''
                const cursorOffset = range.startOffset

                // Simple trigger: if we just typed '@' or are in a sequence like '@ha'
                // Find last '@' before cursor
                const lastAt = textContent.lastIndexOf('@', cursorOffset - 1)

                if (lastAt !== -1) {
                    // Check if it looks like a tag start.
                    const stringAfterAt = textContent.slice(lastAt + 1, cursorOffset)
                    if (!stringAfterAt.includes(' ')) {
                        // Trigger autocomplete
                        const rect = range.getBoundingClientRect()
                        setAutocompleteCoords({ top: rect.bottom, left: rect.left })
                        setAutocompleteSearch(stringAfterAt)
                        setAutocompleteOpen(true)
                    } else {
                        setAutocompleteOpen(false)
                    }
                } else {
                    setAutocompleteOpen(false)
                }
            }
        }
        triggerChange()
    }, [triggerChange])

    // --- Autocomplete Logic ---
    const insertEmotion = (emotion: string) => {
        // Restore selection/range
        const sel = window.getSelection()
        if (!sel?.rangeCount) return

        const range = sel.getRangeAt(0)
        // We assume we are still in the text node where autocomplete triggered.
        if (range.startContainer.nodeType === Node.TEXT_NODE) {
            const textNode = range.startContainer
            const textContent = textNode.textContent || ''
            const cursorOffset = range.startOffset
            const lastAt = textContent.lastIndexOf('@', cursorOffset - 1)

            if (lastAt !== -1) {
                // Split text node
                const before = textContent.slice(0, lastAt)
                const after = textContent.slice(cursorOffset)

                // Modify the text node to contain only 'before' part
                textNode.textContent = before

                // Insert chip
                const chip = createChip(emotion)

                // Insert after text node
                if (textNode.nextSibling) {
                    textNode.parentNode?.insertBefore(chip, textNode.nextSibling)
                } else {
                    textNode.parentNode?.appendChild(chip)
                }

                // Create a new text node for 'after' part
                const afterNode = document.createTextNode(after || '\u00A0') // add nbsp if empty to allow cursor
                if (chip.nextSibling) {
                    chip.parentNode?.insertBefore(afterNode, chip.nextSibling)
                } else {
                    chip.parentNode?.appendChild(afterNode)
                }

                // Move cursor to afterNode start
                range.setStart(afterNode, 1)
                range.collapse(true)
                sel.removeAllRanges()
                sel.addRange(range)

                triggerChange()
            }
        }
        setAutocompleteOpen(false)
    }

    // --- Popover Edit Logic ---
    const handleEditSelect = (newEmotion: string) => {
        if (editingAnchor) {
            // Preserve the original tag style when editing an existing chip so legacy
            // SSML-format scripts stay SSML and new bracket-format scripts stay bracket.
            editingAnchor.dataset.emotion = newEmotion
            const textSpan = editingAnchor.querySelector('span')
            if (textSpan) textSpan.innerText = newEmotion

            const sound = isSoundTag(newEmotion)
            editingAnchor.className = sound
                ? "inline-flex items-center px-1.5 py-0.5 mx-1 rounded text-xs font-medium bg-green-100 text-green-800 select-none cursor-pointer align-middle"
                : "inline-flex items-center px-1.5 py-0.5 mx-1 rounded text-xs font-medium bg-blue-100 text-blue-800 select-none cursor-pointer align-middle"
            const xBtn = editingAnchor.lastChild as HTMLElement
            if (xBtn && xBtn.tagName === 'SPAN') {
                xBtn.className = sound
                    ? "ml-1 hover:text-green-950 cursor-pointer flex items-center"
                    : "ml-1 hover:text-blue-950 cursor-pointer flex items-center"
            }

            triggerChange()
        }
        setEditingAnchor(null)
    }

    // Autocomplete options come straight from the Gemini transcript-tag catalog.
    const autocompleteOptions = AVAILABLE_EMOTIONS
    const editOptions = AVAILABLE_EMOTIONS

    // Filter options for autocomplete
    const filteredAutocompleteOptions = autocompleteOptions.filter(o =>
        o.toLowerCase().includes(autocompleteSearch.toLowerCase())
    )

    const [selectedIndex, setSelectedIndex] = useState(0)

    // Reset selected index when search changes
    useEffect(() => {
        setSelectedIndex(0)
    }, [autocompleteSearch])

    // Key handlers
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (autocompleteOpen) {
            if (e.key === 'ArrowDown') {
                e.preventDefault()
                setSelectedIndex(prev => (prev + 1) % filteredAutocompleteOptions.length)
                return
            }
            if (e.key === 'ArrowUp') {
                e.preventDefault()
                setSelectedIndex(prev => (prev - 1 + filteredAutocompleteOptions.length) % filteredAutocompleteOptions.length)
                return
            }
            if (e.key === 'Enter') {
                e.preventDefault()
                if (filteredAutocompleteOptions[selectedIndex]) {
                    insertEmotion(filteredAutocompleteOptions[selectedIndex])
                }
                return
            }
            if (e.key === 'Escape') {
                e.preventDefault()
                setAutocompleteOpen(false)
                return
            }
        }

        // Normal enter behavior
        if (e.key === 'Enter') {
            // Prevent default newline if we want single line behavior? 
            // Or let it happen. The original was textarea rows, so newlines allowed.
        }
    }

    return (
        <div className="relative w-full">
            <div
                ref={containerRef}
                contentEditable
                className="w-full min-h-[60px] text-base leading-relaxed text-foreground/90 whitespace-pre-wrap rounded-lg border border-black/10 bg-white p-3 focus:outline-none focus:ring-2 focus:ring-brand-500"
                onInput={handleInput}
                onBlur={onTextBlur}
                onFocus={onTextFocus}
                onKeyDown={handleKeyDown}
                style={{ minHeight: '80px' }}
            />

            {/* Autocomplete Popover */}
            {autocompleteOpen && autocompleteCoords && createPortal(
                <div
                    className="absolute z-50 mt-1"
                    style={{ top: autocompleteCoords.top + window.scrollY + 10, left: autocompleteCoords.left + window.scrollX }}
                >
                    <EmotionPopover
                        open={true}
                        variant="command"
                        options={filteredAutocompleteOptions}
                        selectedIndex={selectedIndex}
                        onOpenChange={(open) => !open && setAutocompleteOpen(false)} // force keep open until select or dismiss
                        onSelect={insertEmotion}
                    >
                        <div className="hidden" />
                    </EmotionPopover>
                </div>,
                document.body
            )}

            {/* Edit Popover */}
            {editingAnchor && (
                <EmotionPopover
                    open={true}
                    variant="default" // Default mode with search for editing
                    options={editOptions}
                    onOpenChange={(open) => !open && setEditingAnchor(null)}
                    currentEmotion={editingAnchor.dataset.emotion}
                    onSelect={handleEditSelect}
                >
                    <div
                        className="fixed w-1 h-1 bg-transparent pointer-events-none"
                        style={{
                            top: editingAnchor.getBoundingClientRect().bottom,
                            left: editingAnchor.getBoundingClientRect().left
                        }}
                    />
                </EmotionPopover>
            )}
        </div>
    )
}
